import clsx from 'clsx'
import useForm from 'components/hooks/useForm'
import { Portal } from 'components/utility/portal'
import axios from 'lib/axios'
import { isEmpty } from 'lodash-es'
import React, { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SelectField from '../common/Select'
import { CopyItem } from '../common/sidebars/right/PathSearch'
import { addFieldDef } from '../features/fields/fieldsSlice'
import { copyText } from 'components/utility/copyText'

const InputField = ({ label, required = false, prepend, append, error, ...props }) => {
  const id = useId()
  return (
    <div className="dome-d-flex dome-flex-column">
      {label && (
        <label htmlFor={id} style={{ margin: 0, width: 'fit-content' }} className="dome-p2 dome-color-med-grey">
          {label} {required && '*'}
        </label>
      )}
      <div
        className="dome-d-flex dome-align-center"
        style={{ borderRadius: 6, padding: ' 6px 12px', border: '1px solid var(--darkest-grey, #4A4A4A)' }}
      >
        {prepend}
        <input
          id={id}
          type="text"
          required={required}
          style={{ border: 'none', outline: 'none', flex: 1, fontSize: 16 }}
          {...props}
        />
        {append}
      </div>
      {error && (
        <div style={{ paddingLeft: 12, paddingTop: 3 }} className="dome-d-flex dome-align-center dome-gap3">
          <i className="fa-light fa-circle-exclamation dome-color-red-alert"></i>
          <span className="dome-p3 dome-color-red-alert">{error}</span>
        </div>
      )}
    </div>
  )
}

const normalizePathName = (str) => {
  if (!str) return ''

  return str.trim().toLowerCase().replaceAll(' ', '_')
}

const getInitialValues = (path) => {
  return {
    label: path,
    path: normalizePathName(path),
    type: 'string',
    input_type: 'text',
    question_label: '',
    editable_by: [],
  }
}

const TYPE_OPTIONS = [
  { label: 'integer', value: 'integer' },
  { label: 'number', value: 'number' },
  { label: 'string', value: 'string' },
  { label: 'boolean', value: 'boolean' },
  { label: 'money', value: 'money' },
  { label: 'file', value: 'file' },
]

const INPUT_TYPE_OPTIONS = [
  { label: 'text', value: 'text' },
  { label: 'boolean', value: 'boolean' },
  { label: 'money', value: 'money' },
  { label: 'options', value: 'options' },
  { label: 'select', value: 'select' },
]

const NewPathCreation = ({ initialPath = '', className, onSubmit = () => {}, onCancel = () => {} }) => {
  const dispatch = useDispatch()
  const { roles, formId } = useSelector((state) => state.base)
  const fieldDefs = useSelector((state) => state.fields.field_defs)

  const ROLES_OPTIONS = useMemo(
    () => Object.values(roles).map((role) => ({ label: role.label_singular, value: role.id })),
    [roles]
  )

  const validate = useCallback(
    (fieldName, value) => {
      switch (fieldName) {
        case 'label':
          return isEmpty(value) ? 'label is required.' : ''
        case 'path':
          if (isEmpty(value)) return 'path is required.'
          if (fieldDefs[value] || fieldDefs[`form.${value}`]) {
            return 'this path already exists. Please choose a unique name.'
          }
          return ''
        case 'question_label':
          return isEmpty(value) ? 'question is required.' : ''
        case 'type':
          return isEmpty(value) ? 'type must be selected.' : ''
        case 'input_type':
          return isEmpty(value) ? 'input type must be selected.' : ''
        case 'editable_by':
          return isEmpty(value) ? 'at least one role must be selected.' : ''
        default:
          return ''
      }
    },
    [fieldDefs]
  )

  const { values, setValues, errors, handleChange, validateAllFields, setErrors } = useForm(
    getInitialValues(initialPath),
    validate
  )

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateAllFields()) return
    try {
      const { data } = await axios.post(`/forms/v3/${formId}/create_path`, {
        'form': { ...values, path: `form.${values['path']}`, editable_by: values['editable_by']?.map((e) => e.value) },
      })
      onSubmit(values)
      setValues(getInitialValues(initialPath))
      dispatch(addFieldDef({ key: data.path, value: data.field }))
    } catch (error) {
      console.log('failed submit new path', error)
    }
  }

  const isFormInvalid = Object.values(errors).some((error) => error)

  return (
    <form onSubmit={handleSubmit} className="dome-full-width dome-d-flex dome-flex-column dome-gap24">
      <div className="dome-d-flex dome-align-center dome-gap12">
        <i className="fa-light fa-xl fa-circle-nodes dome-color-dark-green"></i>
        <span className="dome-h2 dome-color-dark-green">create path</span>
      </div>
      <div className="dome-d-flex dome-flex-column dome-gap12">
        <span className="dome-p2 dome-color-med-grey">Complete required values to create a path for this form.</span>
        <div className="dome-d-flex dome-flex-column dome-gap6">
          <InputField
            name="label"
            label="label"
            required={true}
            value={values['label']}
            error={errors['label']}
            onChange={(e) => handleChange('label', e.target.value)}
            onBlur={(e) => {
              if (isEmpty(values['path'])) handleChange('path', normalizePathName(values['label']))
            }}
            placeholder="search value"
          />
          <InputField
            name="path"
            label="path"
            required={true}
            value={values['path']}
            error={errors['path']}
            placeholder="search value"
            onChange={(e) => handleChange('path', e.target.value)}
            prepend={<span className="dome-p1 dome-text-w500 dome-color-dark-green">form.</span>}
            append={<CopyItem onCopy={() => copyText("form." + values['path'])} />}
          />
          <InputField
            required={true}
            name="question_label"
            label="question"
            value={values['question_label']}
            error={errors['question_label']}
            onChange={(e) => handleChange('question_label', e.target.value)}
          />
          <div className="dome-d-flex dome-justify-between dome-gap6">
            <SelectField
              label="type"
              required={true}
              placeholder="type"
              value={values['type']}
              error={errors['type']}
              className="dome-flex-1"
              options={TYPE_OPTIONS}
              optionKey="value"
              onChange={(item) => handleChange('type', item.value)}
            />
            <SelectField
              required={true}
              label="input type"
              className="dome-flex-1"
              placeholder="input type"
              value={values['input_type']}
              error={errors['input_type']}
              options={INPUT_TYPE_OPTIONS}
              optionKey="value"
              onChange={(item) => handleChange('input_type', item.value)}
            />
          </div>
          <SelectField
            multi={true}
            required={true}
            label="editable by"
            placeholder="editable by"
            value={values['editable_by']}
            error={errors['editable_by']}
            options={ROLES_OPTIONS}
            optionKey="value"
            onChange={(value) => handleChange('editable_by', value)}
          />
        </div>
      </div>
      <div className="dome-d-flex dome-justify-between">
        <button
          type="button"
          onClick={onCancel}
          className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-cancel"
        >
          cancel
        </button>
        <button
          type="submit"
          disabled={isFormInvalid}
          className={clsx('dome-btn dome-btn-base', isFormInvalid ? 'dome-btn-disabled' : 'dome-btn-dark-green')}
        >
          create
        </button>
      </div>
    </form>
  )
}

const CreateNewPathModal = forwardRef(({ open = false, onClose = () => {}, ...props }, ref) => {
  const [show, setShow] = useState(open)
  const overlayRef = useRef(null)

  useEffect(() => {
    setShow(open)
  }, [open])

  const handleClose = () => {
    setShow(false)
    onClose()
  }

  const handleClickOutsideModal = (e) => {
    if (e.target !== overlayRef.current) return
    handleClose()
  }

  useEffect(() => {
    if (show) {
      document.body.classList.add('interstitial-activated')
    } else {
      document.body.classList.remove('interstitial-activated')
    }
    return () => document.body.classList.remove('interstitial-activated')
  }, [show])

  return (
    <Portal>
      <div ref={ref} className={clsx('create-new-path-modal-overlay dome-full-width dome-full-height', show && 'show')}>
        <div
          ref={overlayRef}
          onClick={handleClickOutsideModal}
          className="dome-full-width dome-full-height dome-d-flex dome-align-center dome-justify-center"
        >
          <div className="create-new-path-modal-container">
            <div className="create-new-path-modal-content">
              <NewPathCreation {...props} onSubmit={handleClose} onCancel={handleClose} />
            </div>
          </div>
        </div>
      </div>
    </Portal>
  )
})

CreateNewPathModal.displayName = 'CreateNewPathModal'

export default CreateNewPathModal
